// captureChart — small reusable helper for "capture-then-publish" flows.
//
// Extracted (2026-06) from LiqMapPage.onShareToShadow so the same
// machinery can be reused by CyclesMapView and any future analysis
// surface. The helper deliberately covers ONLY the mechanical part of
// the capture pipeline (watermark injection + htmlToImage.toBlob +
// cleanup); page-specific concerns (state-derived direction, permalink
// serialisation, etc.) stay in their respective pages.
//
// Why imperative watermark injection (instead of mounting the watermark
// as a React node hidden via opacity/visibility): a React-mounted
// watermark causes flicker/race conditions during the snapshot and
// makes opacity toggling unreliable across browsers. Appending a fresh
// `<img>` right before the snapshot, then removing it, guarantees the
// watermark is on-screen for exactly the time the snapshot needs and
// never leaks into normal viewing.
import { toBlob as htiToBlob } from "html-to-image";

const DEFAULT_WATERMARK_STYLE = {
  position: "absolute",
  top: "12px",
  right: "60px",
  width: "12%",
  height: "auto",
  maxWidth: "180px",
  opacity: "0.85",
  pointerEvents: "none",
  zIndex: "25",
};

/**
 * Capture a DOM container as a PNG Blob with a watermark applied
 * imperatively for the duration of the snapshot.
 *
 * @param {object} opts
 * @param {HTMLElement} opts.target          — the element to capture (required)
 * @param {string} opts.watermarkUrl         — image url for the watermark
 *                                             (skip injection when falsy)
 * @param {object} [opts.watermarkStyle]     — overrides for the default style
 * @param {string} [opts.backgroundColor]    — bg for the captured PNG
 *                                             (default "#0a0a0c")
 * @param {number} [opts.pixelRatio]         — DPR cap for the snapshot
 *                                             (default min(devicePixelRatio, 2))
 * @returns {Promise<Blob|null>}             — png Blob or null on failure
 */
export async function captureChartAsBlob({
  target,
  watermarkUrl,
  watermarkStyle,
  backgroundColor = "#0a0a0c",
  pixelRatio,
} = {}) {
  if (!target || typeof target.appendChild !== "function") return null;

  const wm = watermarkUrl ? document.createElement("img") : null;
  if (wm) {
    wm.src = watermarkUrl;
    wm.alt = "";
    wm.setAttribute("aria-hidden", "true");
    const styleObj = { ...DEFAULT_WATERMARK_STYLE, ...(watermarkStyle || {}) };
    wm.style.cssText = Object.entries(styleObj)
      .map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase())}:${v}`)
      .join(";");
    target.appendChild(wm);
    // Two RAFs guarantee paint commits before toBlob walks the tree.
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
  }

  const dpr =
    typeof pixelRatio === "number"
      ? pixelRatio
      : Math.min(
          typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
          2
        );

  let blob = null;
  try {
    blob = await htiToBlob(target, {
      backgroundColor,
      pixelRatio: dpr,
      cacheBust: false,
    });
  } catch (err) {
    console.error("[captureChart] capture failed:", err);
    blob = null;
  } finally {
    if (wm) {
      try {
        target.removeChild(wm);
      } catch {
        /* already removed */
      }
    }
  }
  return blob;
}

/**
 * Best-effort mobile native-share offer. Fire-and-forget — the canonical
 * publish path is the modal; this only opens the OS share sheet when
 * available so users can also stash the image elsewhere (Telegram,
 * Twitter, Photos…). Never throws.
 */
export function tryNativeShare(blob, { title } = {}) {
  if (!blob || typeof navigator === "undefined") return;
  try {
    const file = new File([blob], `chart-${Date.now()}.png`, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      navigator.share({ files: [file], title: title || "Chart" }).catch(() => {
        /* user dismissed */
      });
    }
  } catch {
    /* canShare not available */
  }
}
